import { toast } from 'react-toastify';
import moment from 'moment';

export const DEFAULT_TAG_COLOR = {
  hex: '#EA475C',
  rgb: { r: 234, g: 71, b: 92 },
};

export function getVersion(date: any) {
  return moment().diff(date, 'days') === 0 ? `?v=${moment(date).format('HHmmss')}` : '';
}

export function getPanoramaIndex(panoramas: any[], id: number) {
  return panoramas.findIndex((p: any) => p.id === id);
}

export function getQueryVariable(query: string, variable: string) {
  if (!query || !variable) return false;
  query = decodeURIComponent(query.split('?')[1]);
  const vars = query.split('&');
  for (let i = 0; i < vars.length; i += 1) {
    const pair = vars[i].split('=');
    if (pair[0] === variable) {
      return pair[1];
    }
  }
  return false;
}

export function getAllQueryVariables(query: string, prefix = '&') {
  if (!query) return '';
  return `${prefix}${query.split('?').pop()}`;
}

export function getYoutubeVideoQueryVariable(query: string, variable: string) {
  if (!query || !variable) return '';
  if (getQueryVariable(query, variable)) {
    return getQueryVariable(query, variable);
  }
  return query.split('/').pop();
}

export function getVideoLink(
  url: string,
  settings?: { autoplay?: number | boolean; mute?: boolean; subtitles?: string },
) {
  if (!url) return '';
  let autoplay;
  if (settings && typeof settings.autoplay === 'boolean') {
    autoplay = settings.autoplay ? 1 : 0;
  } else {
    autoplay = settings?.autoplay === undefined ? 1 : settings?.autoplay;
  }

  const mute = settings?.mute === undefined ? false : settings?.mute;
  let muteQueryParam: string = mute ? '&mute=1' : '';

  let urlC: string[] = [];
  if (url.includes('https://vimeo.com/')) {
    urlC = url.split('https://vimeo.com/');
  } else if (url.includes('https://player.vimeo.com/video/')) {
    urlC = url.split('https://player.vimeo.com/video/');
  }
  if (urlC.length > 1) {
    muteQueryParam = mute ? '&&muted=1' : '';
    const subtitlesParam = settings?.subtitles ? `&&texttrack=${settings.subtitles}` : '';
    return `https://player.vimeo.com/video/${urlC[1]}?autoplay=${autoplay}&title=0&byline=0&portrait=0${muteQueryParam}${subtitlesParam}`;
  }
  const subtitlesParam = settings?.subtitles ? `&&hl=${settings.subtitles}` : '';
  return `https://www.youtube.com/embed/${getYoutubeVideoQueryVariable(
    url,
    'v',
  )}?autoplay=${autoplay}${muteQueryParam}${subtitlesParam}`;
}

export function getBaseURL() {
  return window.location.href.split('?')[0];
}

export function buildLink(s: string, rotation: any, m: any = null) {
  let link = `${getBaseURL()}?s=${s}&x=${rotation.x}&y=${rotation.y}`;
  if (m) link += `&m=${m}`;
  const ticket = new URLSearchParams(window.location.search).get('ticket');
  if (ticket && ticket.length > 0) link += `&ticket=${ticket}`;
  return link;
}

export function hexToRgb(hex: string = DEFAULT_TAG_COLOR.hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result
    ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16),
      }
    : DEFAULT_TAG_COLOR.rgb;
}

export function copyToClipboard(text: string) {
  const textarea = document.createElement('textarea');
  textarea.textContent = text;
  textarea.style.position = 'fixed'; // Prevent scrolling to bottom of page in Microsoft Edge.
  document.body.appendChild(textarea);
  textarea.select();
  try {
    return document.execCommand('copy'); // Security exception may be thrown by some browsers.
  } catch (ex) {
    toast.warn('Copy to clipboard failed.', ex);
    return false;
  } finally {
    document.body.removeChild(textarea);
  }
}

export const downloadFloorPlan = async (url: string, plan: any) => {
  const pdfName = plan?.name || '';
  const pdfType = plan?.type || 'pdf';
  try {
    const response = await fetch('v1/public/downloadPdf', {
      method: 'POST',
      body: JSON.stringify({ url, pdfName }),
    });
    const blob = await response.blob();
    const urlTwo = (window as any).URL.createObjectURL(new Blob([blob]));
    const link = document.createElement('a');
    link.href = urlTwo;
    link.setAttribute('download', `${pdfName}.${pdfType}`);
    document.body.appendChild(link);
    link.click();
    link.remove();
  } catch (err) {
    toast.error(err.message);
  }
};

export const validateEmail = (email: string): boolean => {
  const regex = /^([\w-.]+@([\w-]+\.)+[\w-]{2,})+?$/;
  return regex.test(email);
};

export const round = (value, decimals) =>
  Number(`${Math.round(Number(`${value}e${decimals}`))}e-${decimals}`);

export const openLinkInNewTab = (linkText: string) => {
  const link = document.createElement('a');
  link.href = linkText;
  link.target = '_blanc';
  document.body.appendChild(link);
  link.click();
  link.remove();
};

export const getBucketLink = (append: string) =>
  `${process.env.NEXT_PUBLIC_CDN}/ticketing/${append}`;
